import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import styled from 'styled-components';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIBox from 'UIComponents/layout/UIBox';
import { FLINT, KOALA, OLAF, CALYPSO, SORBET } from 'HubStyleTokens/colors';
import UIImage from 'UIComponents/image/UIImage';
import H6 from 'UIComponents/elements/headings/H6';
import { device } from '../../../constants/StyledComponentConstants';
import { useState } from 'react';
import SkeletonThumbnail from './SkeletonThumbnail';
import CompletedIcon from '../../ContentCards/ReducedContentCard/subcomponents/CompletedIcon';
import { FormattedList, FormattedMessage } from '../../I18n';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import Small from 'UIComponents/elements/Small';
const FlintTransparentBg = 'rgb(153, 172, 194, 0.75)';
const PROGRESS_BAR_HEIGHT = 3;
const RelativeDiv = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__RelativeDiv",
  componentId: "tg1b40-0"
})(["position:relative;height:fit-content;"]);
const ThumbnailOverlay = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailOverlay",
  componentId: "tg1b40-1"
})(["position:absolute;top:0;bottom:0;left:0;right:0;color:", ";border-radius:3px 3px 0 0;overflow:hidden;", " @media screen and ", "{border-radius:0;}"], ({
  $isGhost
}) => $isGhost ? FLINT : OLAF, ({
  $isGhost,
  $disableGradient
}) => $isGhost ? `background: ${FlintTransparentBg};` : !$disableGradient ? 'box-shadow: inset 0 70px 60px -60px #000;' : '', device.mobile);
const ThumbnailFooter = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailFooter",
  componentId: "tg1b40-2"
})(["border-radius:0 0 3px 3px;background:", ";position:absolute;bottom:0;left:0;right:0;height:30px;white-space:nowrap;", ";"], ({
  $isGhost
}) => $isGhost ? FlintTransparentBg : 'rgb(0, 0, 0, 0.6);`', ({
  $isGhost
}) => $isGhost ? `color: ${KOALA}` : '');
const ThumbnailHeader = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailHeader",
  componentId: "tg1b40-3"
})(["padding:14px 12px;position:absolute;width:100%;"]);
const ThumbnailCenter = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailCenter",
  componentId: "tg1b40-4"
})(["position:absolute;width:100%;height:100%;top:0;display:flex;justify-content:center;align-items:center;"]);
const ContentTitle = styled.span.withConfig({
  displayName: "ThumbnailOverlayed__ContentTitle",
  componentId: "tg1b40-5"
})(["color:", ";", ";"], OLAF, ({
  $isGhost
}) => $isGhost ? `opacity: .5` : '');
const ThumbnailImage = styled(UIImage).withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailImage",
  componentId: "tg1b40-6"
})(["border-radius:3px;"]);
const DarkOverlay = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__DarkOverlay",
  componentId: "tg1b40-7"
})(["background-color:rgba(0,0,0,0.5);position:absolute;top:0;left:0;bottom:0;right:0;"]);
const ProgressBarContainer = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__ProgressBarContainer",
  componentId: "tg1b40-8"
})(["position:absolute;height:", "px;width:100%;"], PROGRESS_BAR_HEIGHT);
const ProgressBar = styled.div.withConfig({
  displayName: "ThumbnailOverlayed__ProgressBar",
  componentId: "tg1b40-9"
})(["height:100%;background-color:", ";"], ({
  $progressamount
}) => $progressamount === 1 ? CALYPSO : CALYPSO);
export const CategoryLabel = styled.span.withConfig({
  displayName: "ThumbnailOverlayed__CategoryLabel",
  componentId: "tg1b40-10"
})(["color:", ";text-transform:capitalize;", ";"], OLAF, ({
  $isGhost
}) => $isGhost ? `opacity: .5` : '');
const ThumbnailHeading = styled(H6).withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailHeading",
  componentId: "tg1b40-11"
})(["font-weight:500;"]);
const CategoryContainer = styled(Small).withConfig({
  displayName: "ThumbnailOverlayed__CategoryContainer",
  componentId: "tg1b40-12"
})(["display:block;padding-top:1px;"]);
const ThumbnailFooterContent = styled(UIFlex).withConfig({
  displayName: "ThumbnailOverlayed__ThumbnailFooterContent",
  componentId: "tg1b40-13"
})(["padding:", "px 10px 1px;height:100%;"], PROGRESS_BAR_HEIGHT);
const CardCompletedWrapper = styled(UIFlex).withConfig({
  displayName: "ThumbnailOverlayed__CardCompletedWrapper",
  componentId: "tg1b40-14"
})(["gap:4px;"]);

const ThumbnailOverlayedCategories = ({
  categories,
  isGhost
}) => {
  return /*#__PURE__*/_jsx(CategoryContainer, {
    children: /*#__PURE__*/_jsx(FormattedList, {
      lastWordConnector: ', ',
      twoWordsConnector: ', ',
      wordsConnector: ', ',
      children: categories.map(({
        label
      }) => /*#__PURE__*/_jsx(CategoryLabel, {
        $isGhost: !!isGhost,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `learningCenter.categories.${label}`
        })
      }, label))
    })
  });
};

const CardProgressBar = ({
  disabled,
  totalTime,
  remainingTime
}) => {
  return totalTime !== remainingTime ? /*#__PURE__*/_jsx(UINanoProgress, {
    "aria-label": "Time remaining",
    className: "time-remaining-time-graph__graph",
    color: disabled ? 'rgb(255,255,255,.5)' : SORBET,
    value: (totalTime - remainingTime) / totalTime * 100
  }) : null;
};

export const CardCompletedMessage = () => {
  return /*#__PURE__*/_jsxs(CardCompletedWrapper, {
    align: "center",
    children: [/*#__PURE__*/_jsx(CompletedIcon, {
      style: {
        bottom: 0
      }
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: `learningCenter.completedIcontext`
    })]
  });
};

const ThumbnailOverlayed = ({
  thumbnailFooter,
  thumbnailCenter,
  title,
  header,
  headerIcons,
  completed = false,
  isGhost = false,
  disableGradient = false,
  previewThumbnailRectangleUrl,
  totalTime,
  remainingTime,
  categories
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const hasImage = previewThumbnailRectangleUrl && imageLoaded;
  return /*#__PURE__*/_jsxs(RelativeDiv, {
    "data-test-id": "card-thumbnail",
    children: [completed ? /*#__PURE__*/_jsx(DarkOverlay, {}) : null, /*#__PURE__*/_jsx(ThumbnailImage, {
      alt: "Course thumbnail",
      onLoad: () => setImageLoaded(true),
      src: previewThumbnailRectangleUrl,
      style: {
        display: hasImage ? 'block' : 'none'
      }
    }), !hasImage && /*#__PURE__*/_jsx(SkeletonThumbnail, {
      noShimmer: true
    }), /*#__PURE__*/_jsxs(ThumbnailOverlay, {
      $disableGradient: disableGradient,
      $isGhost: isGhost,
      children: [thumbnailCenter && /*#__PURE__*/_jsx(ThumbnailCenter, {
        children: thumbnailCenter
      }), /*#__PURE__*/_jsx(ThumbnailHeader, {
        children: /*#__PURE__*/_jsxs(UIFlex, {
          justify: "between",
          children: [/*#__PURE__*/_jsx(UIBox, {
            children: /*#__PURE__*/_jsxs(ThumbnailHeading, {
              children: [title && /*#__PURE__*/_jsxs(ContentTitle, {
                $isGhost: isGhost,
                children: [title, " "]
              }), header, categories && /*#__PURE__*/_jsx(ThumbnailOverlayedCategories, {
                categories: categories,
                isGhost: isGhost
              })]
            })
          }), headerIcons && /*#__PURE__*/_jsx(UIBox, {
            children: headerIcons
          })]
        })
      }), /*#__PURE__*/_jsxs(ThumbnailFooter, {
        $isGhost: isGhost,
        children: [/*#__PURE__*/_jsx(ProgressBarContainer, {
          children: completed ? /*#__PURE__*/_jsx(ProgressBar, {
            $progressamount: 100
          }) : /*#__PURE__*/_jsx(CardProgressBar, {
            disabled: isGhost,
            remainingTime: remainingTime,
            totalTime: totalTime
          })
        }), /*#__PURE__*/_jsxs(ThumbnailFooterContent, {
          align: 'center',
          direction: 'row',
          justify: 'between',
          children: [completed && /*#__PURE__*/_jsx(CardCompletedMessage, {}), thumbnailFooter]
        })]
      })]
    })]
  });
};

export default ThumbnailOverlayed;